import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { useKey, usePrevious } from 'react-use';
import styled from 'styled-components';

import { DivisionChange } from '@sorare/core/src/__generated__/globalTypes';
import { Modal } from '@sorare/core/src/atoms/Modal';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { useBodyLock } from '@sorare/core/src/hooks/useBodyLock';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';

import { useMarkStoriesAsRead } from 'hooks/stories/useMarkStoriesAsRead';

import { CardLeveledUp } from '../CardLeveledUp';
import { CardRewardsReveal } from '../CardRewardsReveal';
import { CompletedOnboardingTask } from '../CompletedOnboardingTask';
import { CompletedStreakTask } from '../CompletedStreakTask';
import { Intro } from '../Intro';
import { Promotion } from '../Promotion';
import { Relegation } from '../Relegation';
import { RewardsRecap } from '../RewardsRecap';
import { useRewardsVerification } from '../RewardsRecap/useRewardsVerification';
import { StoryState } from '../types';
import type {
  RewardsFlow_cardXpGradeThreshold,
  RewardsFlow_leveledUpCard,
  RewardsFlow_managerTaskInterface,
  RewardsFlow_thresholdsStreakTask,
} from './__generated__/index.graphql';
import {
  RewardsFlow_so5Fixture,
  RewardsFlow_so5Reward,
} from './__generated__/index.graphql';

const Wrapper = styled(Vertical).attrs({ gap: 0, center: true })`
  position: fixed;
  inset: 0;
  overflow: hidden;
  background-color: var(--c-black);
`;

type RewardsFlowProps = {
  storyId?: string;
  so5Fixture?: RewardsFlow_so5Fixture;
  so5Rewards: RewardsFlow_so5Reward[];
  leveledUpCards?: RewardsFlow_leveledUpCard[] | null;
  completedThresholdsStreakTasks?: RewardsFlow_thresholdsStreakTask[] | null;
  completedOnboardingTasks?: RewardsFlow_managerTaskInterface[] | null;
  gradeThresholds?: RewardsFlow_cardXpGradeThreshold[];
  initialStep?: StoryState;
  onClose?: () => void;
};
export const RewardsFlow = ({
  storyId,
  so5Fixture,
  so5Rewards: so5RewardsProp,
  leveledUpCards,
  completedThresholdsStreakTasks,
  completedOnboardingTasks,
  gradeThresholds,
  initialStep,
  onClose,
}: RewardsFlowProps) => {
  const {
    flags: { enableFixtureStoryCompletedTasks = false },
  } = useFeatureFlags();
  const [so5Rewards, setSo5Rewards] = useState<RewardsFlow_so5Reward[]>();
  if (!so5Rewards && so5RewardsProp?.length > 0) {
    setSo5Rewards(so5RewardsProp);
  }

  const [open, setOpen] = useState(true);
  const [storyState, setStoryState] = useState<StoryState>(
    initialStep || 'intro'
  );
  const prevStoryState = usePrevious(storyState);
  const { readStories: readFixtureStories, loading } = useMarkStoriesAsRead();
  const { unclaimedRewardsByType } = useRewardsVerification(so5Rewards || []);

  const finalOnClose = () => {
    setOpen(false);
    onClose?.();
    if (storyId && !loading) {
      readFixtureStories({
        storyIds: [storyId],
      });
    }
  };
  useKey('Escape', finalOnClose);
  useBodyLock(open);

  const hasUnclaimedReward =
    unclaimedRewardsByType.total > 0 || !!completedOnboardingTasks?.length;
  const hasCardRewards = unclaimedRewardsByType.card.length > 0;

  const hasPromotion = so5Fixture?.mySo5Rankings?.some(
    ranking => ranking.eligibleDivisionChange === DivisionChange.PROMOTED
  );
  const hasRelegation = so5Fixture?.mySo5Rankings?.some(
    ranking => ranking.eligibleDivisionChange === DivisionChange.RELEGATED
  );
  const hasCardLeveledUps = (leveledUpCards?.length || 0) > 0;
  const hasCompletedStreakTasks =
    (completedThresholdsStreakTasks?.length || 0) > 0;
  const hasCompletedOnboardingTasks =
    (completedOnboardingTasks?.length || 0) > 0 &&
    enableFixtureStoryCompletedTasks;

  return (
    <Modal>
      <Wrapper>
        {storyState === 'intro' && (
          <Intro
            so5Fixture={so5Fixture}
            so5Rewards={so5Rewards || []}
            onNext={
              hasPromotion ||
              hasRelegation ||
              hasCardLeveledUps ||
              hasUnclaimedReward ||
              hasCompletedStreakTasks ||
              hasCompletedOnboardingTasks
                ? () => {
                    if (hasPromotion) {
                      setStoryState('promotion');
                    } else if (hasRelegation) {
                      setStoryState('relegation');
                    } else if (hasCardLeveledUps) {
                      setStoryState('cardLeveledUp');
                    } else if (hasCompletedStreakTasks) {
                      setStoryState('completedStreakTask');
                    } else if (hasCompletedOnboardingTasks) {
                      setStoryState('completedOnboardingTask');
                    } else if (hasUnclaimedReward) {
                      setStoryState('rewardsRecap');
                    }
                  }
                : undefined
            }
            onClose={finalOnClose}
          />
        )}
        {storyState === 'promotion' && (
          <Promotion
            so5Fixture={so5Fixture}
            onNext={
              hasRelegation ||
              hasCardLeveledUps ||
              hasCompletedStreakTasks ||
              hasCompletedOnboardingTasks ||
              hasUnclaimedReward
                ? () => {
                    if (hasRelegation) {
                      setStoryState('relegation');
                    } else if (hasCardLeveledUps) {
                      setStoryState('cardLeveledUp');
                    } else if (hasCompletedStreakTasks) {
                      setStoryState('completedStreakTask');
                    } else if (hasCompletedOnboardingTasks) {
                      setStoryState('completedOnboardingTask');
                    } else if (hasUnclaimedReward) {
                      setStoryState('rewardsRecap');
                    }
                  }
                : undefined
            }
            onClose={finalOnClose}
          />
        )}
        {storyState === 'relegation' && (
          <Relegation
            from={prevStoryState}
            so5Fixture={so5Fixture}
            onNext={
              hasCardLeveledUps ||
              hasCompletedStreakTasks ||
              hasCompletedOnboardingTasks ||
              hasUnclaimedReward
                ? () => {
                    if (hasCardLeveledUps) {
                      setStoryState('cardLeveledUp');
                    } else if (hasCompletedStreakTasks) {
                      setStoryState('completedStreakTask');
                    } else if (hasCompletedOnboardingTasks) {
                      setStoryState('completedOnboardingTask');
                    } else if (hasUnclaimedReward) {
                      setStoryState('rewardsRecap');
                    }
                  }
                : undefined
            }
            onClose={finalOnClose}
          />
        )}
        {storyState === 'cardLeveledUp' && (
          <CardLeveledUp
            from={prevStoryState}
            so5Fixture={so5Fixture}
            leveledUpCards={leveledUpCards || []}
            gradeThresholds={gradeThresholds}
            onNext={
              hasCompletedStreakTasks ||
              hasCompletedOnboardingTasks ||
              hasUnclaimedReward
                ? () => {
                    if (hasCompletedStreakTasks) {
                      setStoryState('completedStreakTask');
                    } else if (hasCompletedOnboardingTasks) {
                      setStoryState('completedOnboardingTask');
                    } else if (hasUnclaimedReward) {
                      setStoryState('rewardsRecap');
                    }
                  }
                : undefined
            }
            onClose={finalOnClose}
          />
        )}
        {storyState === 'completedStreakTask' && (
          <CompletedStreakTask
            from={prevStoryState}
            so5Fixture={so5Fixture}
            completedStreakTasks={completedThresholdsStreakTasks || []}
            onNext={
              hasUnclaimedReward
                ? () => {
                    if (hasCompletedOnboardingTasks) {
                      setStoryState('completedOnboardingTask');
                    } else {
                      setStoryState('rewardsRecap');
                    }
                  }
                : undefined
            }
            onClose={finalOnClose}
          />
        )}
        {storyState === 'completedOnboardingTask' && (
          <CompletedOnboardingTask
            from={prevStoryState}
            so5Fixture={so5Fixture}
            completedOnboardingTasks={completedOnboardingTasks || []}
            onClose={finalOnClose}
            onNext={
              hasUnclaimedReward
                ? () => setStoryState('rewardsRecap')
                : undefined
            }
          />
        )}
        {storyState === 'rewardsRecap' && (
          <RewardsRecap
            from={prevStoryState}
            so5Fixture={so5Fixture}
            so5Rewards={so5Rewards || []}
            taskRewards={
              enableFixtureStoryCompletedTasks
                ? completedOnboardingTasks?.flatMap(
                    task => task.configRewards
                  ) || []
                : []
            }
            onNext={
              hasCardRewards ? () => setStoryState('cardReveal') : undefined
            }
            onClose={finalOnClose}
          />
        )}
        {storyState === 'cardReveal' && (
          <CardRewardsReveal
            so5Fixture={so5Fixture}
            so5Rewards={so5Rewards || []}
            onClose={finalOnClose}
          />
        )}
      </Wrapper>
    </Modal>
  );
};
RewardsFlow.fragments = {
  thresholdsStreakTask: gql`
    fragment RewardsFlow_thresholdsStreakTask on ThresholdsStreakTask {
      id
      ...CompletedStreakTask_thresholdsStreakTask
    }
    ${CompletedStreakTask.fragments.thresholdsStreakTask}
  ` as TypedDocumentNode<RewardsFlow_thresholdsStreakTask>,
  managerTaskInterface: gql`
    fragment RewardsFlow_managerTaskInterface on ManagerTaskInterface {
      id
      configRewards {
        ...RewardsRecap_managerTaskRewardInterface
      }
      ...CompletedOnboardingTask_managerTaskInterface
    }
    ${CompletedOnboardingTask.fragments.managerTaskInterface}
    ${RewardsRecap.fragments.managerTaskRewardInterface}
  ` as TypedDocumentNode<RewardsFlow_managerTaskInterface>,
  cardXpGradeThreshold: gql`
    fragment RewardsFlow_cardXpGradeThreshold on CardXPGradeThreshold {
      ...CardLeveledUp_cardXpGradeThreshold
    }
    ${CardLeveledUp.fragments.cardXpGradeThreshold}
  ` as TypedDocumentNode<RewardsFlow_cardXpGradeThreshold>,
  leveledUpCard: gql`
    fragment RewardsFlow_leveledUpCard on LeveledUpCard {
      id
      ...CardLeveledup_leveledUpCard
    }
    ${CardLeveledUp.fragments.leveledUpCard}
  ` as TypedDocumentNode<RewardsFlow_leveledUpCard>,
  so5Reward: gql`
    fragment RewardsFlow_so5Reward on So5Reward {
      slug
      aasmState
      ...Intro_so5Reward
      ...RewardsRecap_so5Reward
      ...CardRewardsReveal_so5Reward
      ...useRewardsVerification_so5Reward
    }
    ${Intro.fragments.so5Reward}
    ${RewardsRecap.fragments.so5Reward}
    ${CardRewardsReveal.fragments.so5Reward}
    ${useRewardsVerification.fragments.so5Reward}
  ` as TypedDocumentNode<RewardsFlow_so5Reward>,
  so5Fixture: gql`
    fragment RewardsFlow_so5Fixture on So5Fixture {
      slug
      sport
      mySo5Rankings {
        id
        eligibleDivisionChange
      }
      ...Intro_so5Fixture
      ...Promotion_so5Fixture
      ...Relegation_so5Fixture
      ...CardLeveledUp_so5Fixture
      ...CompletedStreakTask_so5Fixture
      ...RewardsRecap_so5Fixture
      ...CardRewardsReveal_so5Fixture
      ...CompletedOnboardingTask_so5Fixture
    }
    ${Intro.fragments.so5Fixture}
    ${Promotion.fragments.so5Fixture}
    ${Relegation.fragments.so5Fixture}
    ${CardLeveledUp.fragments.so5Fixture}
    ${CompletedStreakTask.fragments.so5Fixture}
    ${RewardsRecap.fragments.so5Fixture}
    ${CardRewardsReveal.fragments.so5Fixture}
    ${CompletedOnboardingTask.fragments.so5Fixture}
  ` as TypedDocumentNode<RewardsFlow_so5Fixture>,
};
